/**
 * The server's own count of the orders channel, compared with the tablet's cursor — pure decision.
 *
 * Why this exists: the Events API closes every socket at 24 h and Amplify sometimes stops
 * reconnecting (see socketWatchdog.js). The watchdog reads the socket locally. This is the second,
 * request-free net: the `syncDevice` call the health check already makes every 10 minutes also
 * returns `ordersMaxSeq` (the highest seq in the server's event ledger for `orders/<restaurant>`)
 * and `ordersMaxSeqAgeSeconds` (how long ago that event happened, on the server's clock, so the
 * tablet's clock never enters the age).
 *
 * A server that is ahead of the cursor for more than two minutes means the tablet has not applied
 * an event it should have. Three causes look the same from here and a resync fixes all of them:
 * a publish that failed after the database commit, a reload that restored a stale cursor, and a
 * gap resync that is in backoff. Only one of them is a deaf socket, which a rebuild fixes: the
 * event happened while the subscription was already up AND no data frame has arrived since.
 *
 * App.vue owns the side effects (resync, episode, rebuild, telemetry).
 */

/** An event this young may still be on its way: the push and the cursor catch up on their own. */
export const SEQ_HINT_MIN_AGE_S = 120;

/**
 * @param {object} input
 * @param {number|null} input.srv   `ordersMaxSeq` from syncDevice (null: not the device itself, no restaurant, no events in 72 h)
 * @param {number|null} input.age   `ordersMaxSeqAgeSeconds`, server clock
 * @param {string|null} input.restaurantId         `restaurantId` from the same syncDevice result
 * @param {string|null} input.currentRestaurantId  the restaurant this tablet is logged into now
 * @param {number|null} input.cur   the ledger cursor of `orders/<restaurant>` (null: none yet)
 * @param {number|null} input.upS   seconds since the orders subscription was (re)established (null: it is not up)
 * @param {number|null} input.quietS  seconds since the last DATA frame on any Events channel (null: none since the page loaded)
 * @param {boolean} input.inDeafEpisode  a deaf episode is already open: its recovery owns the socket
 * @returns {'ignore'|'resync'|'resync+recover'}
 */
export function decideSeqHint({ srv, age, restaurantId, currentRestaurantId, cur, upS, quietS, inDeafEpisode }) {
  if (srv == null || age == null) return 'ignore';
  if (!restaurantId || restaurantId !== currentRestaurantId) return 'ignore';
  if (age < SEQ_HINT_MIN_AGE_S) return 'ignore';
  if (cur == null) return 'ignore'; // no cursor to compare with: seedCursor adopts the server's
  if (srv <= cur) return 'ignore'; // equal: in step. Below: a seq reset or a stale read, not a missed event

  if (inDeafEpisode) return 'resync';
  // The event predates the subscription: the socket was not up to receive it.
  if (upS == null || !(age < upS)) return 'resync';
  // A frame arrived after the event (quietS < age): the socket delivers, so the miss is not deafness.
  // No data frame at all since the page loaded counts as quiet.
  if (quietS != null && !(quietS > age)) return 'resync';
  return 'resync+recover';
}
